import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useTranslationApp } from "../../../../lib/i18next";
import {
  MODAL_META_AD_LIBRARY_COMPETITOR_ALL,
  MODAL_META_AD_LIBRARY_PLATFORM_ALL,
} from "../../../../i18n/keysTranslations";
import {
  censorGalleryItemsBeyondVisibility,
  filterGalleryItemsByAllowedPlatforms,
  filterGalleryItemsByCompetitor,
  filterGalleryItemsByPlatform,
  formatCompetitorFavoritesForGallery,
  hasCompetitorAdFavorites,
  normalizeCompetitorName,
} from "../../../../services/competitorAdsGallery";
import { resolveCompetitorAdsPlatform } from "../../../../services/competitorAdExternalUrls";
import {
  ALL_FILTER,
  COMPETITOR_FAVORITES_FOLDER_ID,
  GOOGLE,
  LINKEDIN,
  META,
} from "../../../../utils/constants";
import { logoPlatform } from "../../../../utils/string";
import {
  COMPETITORS_COMPETITOR_FILTER_FIELD,
  COMPETITORS_PLATFORM_FILTER_FIELD,
} from "../constants";
import { getCompetitorFolderContentID, getCompetitorItemId } from "../utils";

const COMPETITORS_PLATFORM_OPTIONS = [ALL_FILTER, META, GOOGLE, LINKEDIN];

const mergeCompetitorFolderMembership = ({
  items,
  folderMembershipByContentID,
}) =>
  (items || []).map((item) => {
    const contentID = getCompetitorFolderContentID(item);
    const folderID = contentID
      ? folderMembershipByContentID?.[contentID]
      : undefined;

    if (!folderID) return item;

    return {
      ...item,
      folderID,
    };
  });

const filterGalleryItemsByFolder = ({
  items,
  folderFilterID,
  folderMembershipByContentID,
}) => {
  const itemsWithFolder = mergeCompetitorFolderMembership({
    items,
    folderMembershipByContentID,
  });

  if (folderFilterID === ALL_FILTER) {
    return itemsWithFolder;
  }

  return itemsWithFolder.filter((item) => item?.folderID === folderFilterID);
};

const getAvailablePlatformOptions = ({ items }) => {
  const platformsInData = new Set(
    items.map((item) => resolveCompetitorAdsPlatform({ pageValue: item }))
  );

  return COMPETITORS_PLATFORM_OPTIONS.filter(
    (platform) => platform === ALL_FILTER || platformsInData.has(platform)
  );
};

export const useCompetitorsFilters = ({
  data,
  businessCompetitors,
  favoritesByPageId,
  folderFilterID = ALL_FILTER,
  folderMembershipByContentID = {},
  allowedPlatforms = null,
  visibilityLimit = null,
}) => {
  const { t } = useTranslationApp();
  const showFavoritesOnly = folderFilterID === COMPETITOR_FAVORITES_FOLDER_ID;

  const {
    control,
    setValue,
    reset: resetFilterForm,
  } = useForm({
    defaultValues: {
      [COMPETITORS_PLATFORM_FILTER_FIELD]: ALL_FILTER,
      [COMPETITORS_COMPETITOR_FILTER_FIELD]: "",
    },
  });

  const selectedCompetitorFilter = useWatch({
    control,
    name: COMPETITORS_COMPETITOR_FILTER_FIELD,
  });
  const selectedPlatform = useWatch({
    control,
    name: COMPETITORS_PLATFORM_FILTER_FIELD,
  });

  const favoriteItems = useMemo(() => {
    const formatted = formatCompetitorFavoritesForGallery({
      favoritesByPageId,
    });

    if (!formatted.length || !data?.length) return formatted;

    const dataByItemId = new Map(
      data.map((item) => [getCompetitorItemId(item), item])
    );

    return formatted.map((item) => {
      if (item?.width && item?.height) return item;

      const sourceItem = dataByItemId.get(getCompetitorItemId(item));
      if (!sourceItem?.width || !sourceItem?.height) return item;

      return {
        ...item,
        width: sourceItem.width,
        height: sourceItem.height,
      };
    });
  }, [data, favoritesByPageId]);

  const platformScopedItems = useMemo(() => {
    const items = showFavoritesOnly ? favoriteItems : data;
    const allowedItems = filterGalleryItemsByAllowedPlatforms({
      items,
      allowedPlatforms,
    });
    if (showFavoritesOnly) return allowedItems;
    return censorGalleryItemsBeyondVisibility({
      items: allowedItems,
      visibilityLimit,
    });
  }, [
    allowedPlatforms,
    data,
    favoriteItems,
    showFavoritesOnly,
    visibilityLimit,
  ]);

  const folderScopedData = useMemo(
    () =>
      filterGalleryItemsByFolder({
        items: platformScopedItems,
        folderFilterID: showFavoritesOnly ? ALL_FILTER : folderFilterID,
        folderMembershipByContentID,
      }),
    [
      folderFilterID,
      folderMembershipByContentID,
      platformScopedItems,
      showFavoritesOnly,
    ]
  );

  const sourceItems = folderScopedData;

  const availablePlatformOptions = useMemo(
    () => getAvailablePlatformOptions({ items: sourceItems }),
    [sourceItems]
  );

  const platformFilterOptions = useMemo(
    () =>
      availablePlatformOptions.map((platform) => ({
        value: platform,
        label:
          platform === ALL_FILTER
            ? t(MODAL_META_AD_LIBRARY_PLATFORM_ALL)
            : t(platform),
        iconSrc: platform === ALL_FILTER ? "" : logoPlatform(platform),
      })),
    [availablePlatformOptions, t]
  );

  const competitorFilterOptions = useMemo(() => {
    const platformItems = filterGalleryItemsByPlatform({
      items: sourceItems,
      platformFilterValue: selectedPlatform,
    });

    const allOption = {
      value: "",
      label: t(MODAL_META_AD_LIBRARY_COMPETITOR_ALL),
    };

    const competitorNamesFromAds = platformItems
      .map((item) => item?.competitor?.trim())
      .filter(Boolean);

    const competitorNamesFromBusiness = (businessCompetitors || [])
      .map((name) => name?.trim())
      .filter(Boolean);

    const competitorNames = [
      ...new Set([...competitorNamesFromAds, ...competitorNamesFromBusiness]),
    ];

    const competitorOptions = competitorNames.map((name) => ({
      value: normalizeCompetitorName(name),
      label: name,
    }));

    return [allOption, ...competitorOptions];
  }, [businessCompetitors, selectedPlatform, sourceItems, t]);

  const galleryDisplayData = useMemo(() => {
    const platformItems = filterGalleryItemsByPlatform({
      items: sourceItems,
      platformFilterValue: selectedPlatform,
    });

    return filterGalleryItemsByCompetitor({
      items: platformItems,
      competitorFilterValue: selectedCompetitorFilter,
    });
  }, [selectedCompetitorFilter, selectedPlatform, sourceItems]);

  const hasFavorites = useMemo(
    () => hasCompetitorAdFavorites(favoritesByPageId),
    [favoritesByPageId]
  );

  const onPlatformFilterChange = useCallback(
    (platform) => {
      setValue(COMPETITORS_COMPETITOR_FILTER_FIELD, "", {
        shouldDirty: false,
        shouldValidate: false,
      });

      if (
        platform !== ALL_FILTER &&
        !availablePlatformOptions.includes(platform)
      ) {
        setValue(COMPETITORS_PLATFORM_FILTER_FIELD, ALL_FILTER, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    },
    [availablePlatformOptions, setValue]
  );

  useEffect(() => {
    if (
      selectedPlatform !== ALL_FILTER &&
      !availablePlatformOptions.includes(selectedPlatform)
    ) {
      setValue(COMPETITORS_PLATFORM_FILTER_FIELD, ALL_FILTER, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [availablePlatformOptions, selectedPlatform, setValue]);

  const resetFilters = useCallback(() => {
    resetFilterForm({
      [COMPETITORS_PLATFORM_FILTER_FIELD]: ALL_FILTER,
      [COMPETITORS_COMPETITOR_FILTER_FIELD]: "",
    });
  }, [resetFilterForm]);

  const resetCompetitorFilter = useCallback(() => {
    setValue(COMPETITORS_COMPETITOR_FILTER_FIELD, "", {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [setValue]);

  return {
    control,
    selectedCompetitorFilter,
    selectedPlatform,
    platformFilterOptions,
    competitorFilterOptions,
    galleryDisplayData,
    showFavoritesOnly,
    hasFavorites,
    onPlatformFilterChange,
    resetFilters,
    resetCompetitorFilter,
  };
};

export default useCompetitorsFilters;
